/**
 * Puzzle number helper. Converts an ISO date (or Date) into a human-
 * readable "Curvle #N" number.
 *
 * Genesis is fixed at 2026-05-01 UTC = Puzzle #1. Today (2026-05-16) =
 * Puzzle #16, launch day (2026-05-18) = Puzzle #18, WC final day
 * (2026-07-19) = Puzzle #80.
 *
 * Why we display the number instead of the date:
 *   - The puzzle activates at 00:00 UTC, but our header used to show the
 *     UTC date string. US East Coast users at 8pm see "tomorrow's" date
 *     because it's already past midnight UTC — confusing.
 *   - Puzzle numbers are timezone-independent. Every player anywhere
 *     in the world plays the same #N at the same time.
 *   - Matches the convention used by Wordle, Connections, Strands.
 */

/** May 1, 2026 UTC midnight. Puzzle #1 plays on this date. */
const GENESIS_MS = Date.UTC(2026, 4, 1); // month 4 = May (0-indexed)

/** Convert an ISO date string (YYYY-MM-DD) OR a Date object to the
 *  puzzle number. Returns 1 for genesis day, 2 for the day after, etc.
 *  Returns 0 if the date is before genesis (shouldn't happen in practice). */
export function puzzleNumber(input: string | Date): number {
  let ms: number;
  if (typeof input === "string") {
    // Parse "YYYY-MM-DD" as UTC midnight to match seed.ts convention.
    ms = Date.parse(input + "T00:00:00Z");
  } else {
    // Treat input as UTC midnight of the input's calendar date.
    ms = Date.UTC(
      input.getUTCFullYear(),
      input.getUTCMonth(),
      input.getUTCDate(),
    );
  }
  if (Number.isNaN(ms)) return 0;
  const diff = ms - GENESIS_MS;
  if (diff < 0) return 0;
  return Math.floor(diff / 86_400_000) + 1;
}

/** Format as "Curvle #16" (or whatever the prefix should be). */
export function puzzleLabel(input: string | Date, prefix = "Curvle"): string {
  const n = puzzleNumber(input);
  if (n <= 0) return prefix;
  return `${prefix} #${n}`;
}

/** Milliseconds until the next UTC midnight (when the next puzzle drops).
 *  Useful for the countdown timer in ResultOverlay. */
export function msUntilNextPuzzle(now: Date = new Date()): number {
  const tomorrow = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  );
  return Math.max(0, tomorrow - now.getTime());
}

/** Format a millisecond duration as "HH:MM:SS". Used by the result-overlay
 *  countdown so players know when tomorrow's kick drops in their local clock. */
export function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}
